<template>
  <div class="flex flex-row items-center bg-white px-4 py-2 md:space-x-4 md:px-12 md:py-3">
    <UiText class="hidden md:block">{{ $t('localizationBar.choosePreferences') }}</UiText>

    <UiSkeleton v-if="status === 'pending'" class="h-8 w-56" />
    <UiDropdown
      v-else-if="data?.length"
      id="localization-bar-country"
      :is-open="isCountryDropdownOpen"
      :toggle="toggleCountryDropdown"
      class="relative z-30"
    >
      <template v-if="currentCountry" #selected>
        <Icon :icon="`flag:${currentCountry?.iso_code.toLowerCase()}-1x1`" class="mr-2 rounded-full" width="24" />
        <UiText class="hidden md:block">
          {{ `${currentCountry[locale.slice(0, 2) as CountryLabel]} | ` }}
        </UiText>
        <UiText>
          {{
            `${currentCountry.currency_iso_code} (${getCurrencySymbol(locale as Locale, currentCountry.currency_iso_code)})`
          }}
        </UiText>
      </template>
      <li
        v-for="country in data"
        :key="country.iso_code"
        class="flex cursor-pointer flex-row items-center justify-between space-x-8 rounded-md p-2 hover:bg-neutral-100"
        @click="selectCountry(country.iso_code)"
      >
        <div class="flex flex-row items-center space-x-4">
          <Icon :icon="`flag:${country.iso_code.toLowerCase()}-1x1`" class="rounded-full" width="24" />
          <UiText
            :class="country.iso_code === currentCountry?.iso_code ? 'font-medium text-neutral-900' : 'text-neutral-500'"
          >
            {{
              `${country[locale.slice(0, 2) as CountryLabel]} | ${country.currency_iso_code} (${getCurrencySymbol(locale as Locale, country.currency_iso_code)})`
            }}
          </UiText>
        </div>
        <IconCheck
          :class="country.iso_code === currentCountry?.iso_code ? 'visible' : 'invisible'"
          class="size-6 fill-neutral-900"
        />
      </li>
    </UiDropdown>
    <UiDropdown
      v-if="locales.length"
      id="localization-bar-language"
      :is-open="isLanguageDropdownOpen"
      :toggle="toggleLanguageDropdown"
      class="relative z-30"
    >
      <template v-if="locale" #selected>
        <IconLanguage class="size-6 fill-neutral-900" />
        <UiText>
          {{ locale }}
        </UiText>
      </template>
      <li
        v-for="localeItem in locales"
        :key="localeItem.code"
        class="flex cursor-pointer flex-row items-center justify-between space-x-8 rounded-md p-2 hover:bg-neutral-100"
        @click="selectLanguage(localeItem.code as Locale)"
      >
        <div class="flex flex-row items-center space-x-4">
          <UiText :class="localeItem.code === locale ? 'font-medium text-neutral-900' : 'text-neutral-500'">
            {{ `${localeItem.code} - ${localeItem.name}` }}
          </UiText>
        </div>
        <IconCheck :class="localeItem.code === locale ? 'visible' : 'invisible'" class="size-6 fill-neutral-900" />
      </li>
    </UiDropdown>
  </div>
  <UiModal
    id="cart-deletion-warning"
    :is-open="!!isModalOpen"
    :close="() => (isModalOpen = null)"
    :title="$t('cartDeletionWarningModal.title')"
    :confirm-label="$t('general.confirm')"
    :cancel-label="$t('general.cancel')"
    :confirm-click="deleteCart"
    :cancel-click="() => (isModalOpen = null)"
  >
    <UiText type="p">{{ $t('cartDeletionWarningModal.content') }}</UiText>
  </UiModal>
</template>

<script setup lang="ts">
import { Icon } from '@iconify/vue';

import type { Locale } from '~/utils/common';

import { useAppStore } from '~/stores/app/appStore';
import { useCartStore } from '~/stores/cart/cartStore';

const { locale, locales, setLocale } = useI18n();
const {
  proxy: { dataLayer },
} = useScriptGoogleTagManager();

const appStore = useAppStore();
const cartStore = useCartStore();

const isModalOpen = ref<string | null>(null);

const { data, status } = useAsyncData('countries', async () => {
  if (!appStore.getCountries(locale.value as Locale)) return await appStore.fetchCountries();

  return appStore.getCountries(locale.value as Locale);
});

const { close: closeCountryDropDown, isOpen: isCountryDropdownOpen, toggle: toggleCountryDropdown } = useDisclosure();
const {
  close: closeLanguageDropDown,
  isOpen: isLanguageDropdownOpen,
  toggle: toggleLanguageDropdown,
} = useDisclosure();

const currentCountry = computed(() => data.value?.find((c) => c.iso_code === appStore.currentCountryCode));

const selectCountry = (countryCode: string): void => {
  if (
    cartStore.cart?.total_price.currency_code !== data.value?.find((c) => c.iso_code === countryCode)?.currency_iso_code
  ) {
    closeCountryDropDown();
    isModalOpen.value = countryCode;

    dataLayer.push({
      content_type: 'country',
      event: 'settings',
      value: countryCode,
    });
  } else {
    appStore.currentCountryCode = countryCode;
    closeCountryDropDown();
  }
};

const selectLanguage = (locale: Locale): void => {
  setLocale(locale as 'en-US' | 'fr-FR');
  closeLanguageDropDown();

  dataLayer.push({
    content_type: 'locale',
    event: 'settings',
    value: locale,
  });
};

const deleteCart = (): void => {
  cartStore.deleteCart(locale.value as Locale).finally(() => {
    if (isModalOpen.value) appStore.currentCountryCode = isModalOpen.value;
    isModalOpen.value = null;
  });
};
</script>
